"use client";
// import * as WagmiCore from "@wagmi/core";
// import { WalletConnectConnector } from "@wagmi/core/connectors/walletConnect";
import { jsonRpcProvider } from "@wagmi/core/providers/jsonRpc";
import type { ThemeController } from "@web3modal/core";
import { EIP6963Connector } from "@web3modal/wagmi";

import { createWeb3Modal } from "@web3modal/wagmi/react";
import { InjectedConnector } from "wagmi/connectors/injected";

import React, { useRef, useState } from "react";
import {
  configureChains,
  createConfig,
  useAccount,
  useNetwork,
  WagmiConfig,
} from "wagmi";
import type { Chain } from "wagmi/chains";

import type { onWalletConnect, onWalletDisconnect } from "actions/wallet";
import logo from "components/common/navigation/images/logo.svg";
import MarginlyPool from "contracts/abi/MarginlyPool";
import { useContracts } from "contracts/react/hooks";
import { useMarginlyApi } from "hooks/api";
import { useChainsIfNotConnected } from "hooks/util";
import type { AppConfig } from "types/common";
import { generateUUID } from "util/fallback";
import { WalletConnectConnector } from "wagmi/connectors/walletConnect";

// import { updateAccountForce } from "@/util/walletconnect";
import { Web3Context } from "./context";
import type { ContextValues } from "./types";
import { ARBITRUM_GOERLI } from "constants/common";

if (typeof window !== "undefined") {
  // required fallback for walletconnect initialize on old devices
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID !== "function"
  ) {
    crypto.randomUUID = generateUUID;
  }
}

const Contracts: React.FC<{
  children: (contracts: ContextValues) => React.ReactNode;
  poolConfig: AppConfig["pools"];
  onWalletConnect?: typeof onWalletConnect;
  onWalletDisconnect?: typeof onWalletDisconnect;
}> = ({ children, poolConfig, onWalletConnect, onWalletDisconnect }) => {
  const account = useAccount();
  const chains = useChainsIfNotConnected();
  const { chain } = useNetwork();
  const chainId = (chain ?? chains?.[0])?.id;

  const { data: _chainSearch } = useMarginlyApi({
    endpoint: "chainSearch",
    // TODO: to be removed when the backend is fixed
    args: [ARBITRUM_GOERLI.id],
  });

  const internalChainId = _chainSearch?.chainInfo.chainId;

  React.useEffect(() => {
    if (account.address) {
      onWalletConnect?.(account.address);
    } else {
      onWalletDisconnect?.();
    }
  }, [account.address, onWalletConnect, onWalletDisconnect]);

  const contracts = React.useMemo(() => {
    const pools = poolConfig.filter((v) => v.chainId === chainId);

    return pools.map(
      (v) =>
        ({
          ...v,
          abi: MarginlyPool,
        } as const),
    );
  }, [poolConfig, chainId]);

  const poolContracts = useContracts(contracts ?? []);
  return <>{children?.({ ...poolContracts, internalChainId })}</>;
};

type ThemeVars = (typeof ThemeController)["state"]["themeVariables"];

const W3M_THEME: ThemeVars = {
  // @ts-ignore
  "--w3m-accent-color": "#72706e",
  "--w3m-background-color": "#fff",
  "--w3m-logo-image-url": logo.src,
};

const internalRpcs: Record<number, string[]> = {
  // 421613: ["https://scn.equilab.io/arb/goerli-nitro/rpc"],
  // 80001: ["https://scn.equilab.io/pol/mumbai/rpc"],
};

export const Web3Provider: React.FC<{
  chains: Chain[];
  projectId: string;
  children?: React.ReactNode;
  poolsEnv?: string;
  wrappersEnv?: string;
  poolConfig: AppConfig["pools"];
  onWalletConnect?: typeof onWalletConnect;
  onWalletDisconnect?: typeof onWalletDisconnect;
}> = ({
  chains,
  projectId,
  children,
  poolConfig,
  onWalletConnect,
  onWalletDisconnect,
}) => {
  const counter = useRef(0);
  const w3mRef = useRef<ReturnType<typeof createWeb3Modal> | null>(null);
  const [provider] = useState(() => [
    jsonRpcProvider({
      rpc: (chain) => {
        const rpcs =
          chain.id in internalRpcs
            ? internalRpcs[chain.id]
            : chain.rpcUrls.default.http;

        return {
          http: rpcs[0],
        };
      },
    }),
    jsonRpcProvider({
      rpc: (chain) => {
        const rpcs = chain.rpcUrls.default.http;
        const index = counter.current++ % rpcs.length;

        return {
          http: chain.rpcUrls.default.http[index],
        };
      },
    }),
  ]);

  const [{ publicClient }] = React.useState(() =>
    configureChains(chains, provider, { retryCount: 1 }),
  );

  const [wagmiConfig] = React.useState(() =>
    createConfig({
      autoConnect: true,
      connectors: [
        new WalletConnectConnector({
          chains,
          /* Connector: WalletConnectConnector, */
          options: { projectId, showQrModal: false },
        }),
        new EIP6963Connector({ chains }),
        new InjectedConnector({ chains, options: { shimDisconnect: true } }),
      ],

      publicClient,
    }),
  );

  if (!w3mRef.current) {
    w3mRef.current = createWeb3Modal({ wagmiConfig, projectId, chains });
    w3mRef.current.setThemeVariables(W3M_THEME);
  }

  return (
    <>
      <WagmiConfig config={wagmiConfig}>
        <Contracts
          poolConfig={poolConfig}
          onWalletConnect={onWalletConnect}
          onWalletDisconnect={onWalletDisconnect}
        >
          {(contracts) => {
            return (
              <Web3Context.Provider value={contracts}>
                {children}
                {/* <W3MInjectBalances /> */}
              </Web3Context.Provider>
            );
          }}
        </Contracts>
      </WagmiConfig>
    </>
  );
};

export const useWeb3Context = () => React.useContext(Web3Context);
